import { LOCAL_COLORS } from 'styles/constants';

/**
 * Matches #rgb and #rrggbb.
 *
 * Alpha (#rgba / #rrggbbaa) is deliberately rejected: a translucent button
 * shows the page through it, so the contrast we compute in
 * `getReadableTextColor` would be wrong and the label could end up unreadable.
 *
 * Note that Contentful's own validation on componentCtaButton.customColor and
 * .textColor is currently looser than this and still accepts alpha, so a value
 * an editor can save may be rejected here and fall back to the primary color.
 * Tightening the CMS rule to match would make that fail loudly instead.
 */
export const HEX_COLOR_REGEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && HEX_COLOR_REGEX.test(value.trim());

/**
 * Returns the trimmed HEX color when valid, otherwise `undefined`.
 *
 * Use this before interpolating CMS-provided colors into CSS so that arbitrary
 * strings from Contentful never reach a stylesheet.
 */
export const sanitizeHexColor = (value?: string | null): string | undefined =>
  isHexColor(value) ? value.trim() : undefined;

/** Expands #rgb to its 6-digit form and splits it into channels. */
const toRgbChannels = (hex: string): [number, number, number] => {
  const raw = hex.trim().slice(1);
  const expanded =
    raw.length === 3
      ? raw
          .split('')
          .map((char) => char + char)
          .join('')
      : raw;

  return [0, 2, 4].map((offset) =>
    parseInt(expanded.slice(offset, offset + 2), 16),
  ) as [number, number, number];
};

/** WCAG relative luminance (https://www.w3.org/TR/WCAG21/#dfn-relative-luminance). */
const getRelativeLuminance = (hex: string) => {
  const [red, green, blue] = toRgbChannels(hex).map((channel) => {
    const ratio = channel / 255;

    return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/**
 * WCAG AA minimum for body-size text. The CTA label is 14px bold, which is
 * below the 18.66px bold threshold for "large text", so the stricter ratio
 * applies.
 */
export const MINIMUM_CONTRAST_RATIO = 4.5;

const getContrastRatio = (colorA: string, colorB: string) => {
  const luminanceA = getRelativeLuminance(colorA);
  const luminanceB = getRelativeLuminance(colorB);
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Picks whichever of our default label colors is more readable on `background`.
 *
 * Editors can choose any HEX code for a custom CTA, so we can't rely on a
 * hand-maintained palette map the way `getContrastingColor` does. Without this,
 * a light custom background keeps Radiance's white label and is unreadable.
 *
 * `background` must already have passed `sanitizeHexColor`.
 */
export const getReadableTextColor = (background: string) =>
  getContrastRatio(background, LOCAL_COLORS.textDefault) >=
  getContrastRatio(background, LOCAL_COLORS.textInverse)
    ? LOCAL_COLORS.textDefault
    : LOCAL_COLORS.textInverse;

/**
 * Whether `text` on `background` clears WCAG AA for body-size text.
 *
 * `getReadableTextColor` only promises the better of two options, which on a
 * mid-tone background can still be too low. Use this to tell editors when the
 * color they picked is unreadable either way.
 *
 * Both colors must already have passed `sanitizeHexColor`.
 */
export const hasReadableContrast = (background: string, text: string) =>
  getContrastRatio(background, text) >= MINIMUM_CONTRAST_RATIO;
